// ./src/contexts/CarStatsContext/CarStatsContext.tsx
import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from "react";
import dayjs from "dayjs";

import { CarTotalSummary } from "@/utils/api/CarTotalSummaryService";
import { CarMonthlySummary } from "@/utils/api/CarMonthlySummaryService";

import {
  useCarTotalSummaries,
  UseCarTotalSummariesOptions
} from "@/hooks/useCarTotalSummaries";

import {
  useCarMonthlySummaries,
  UseCarMonthlySummariesOptions,
} from "@/hooks/useCarMonthlySummaries";

import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { useCarsContext } from "@/contexts/CarsContext";

// ============================================
// Types
// ============================================

export interface MonthYear {
  year: number;
  month: number;
}

export interface MonthlyAggregates {
  // Refuels
  refuelsCount: number;
  refuelsCost: number;
  refuelsVolume: number;
  // Maintenance (subset of expenses)
  maintenanceCount: number;
  maintenanceCost: number;
  // Other expenses (total expenses - maintenance)
  otherExpensesCount: number;
  otherExpensesCost: number;
  // Total cost (refuels + all expenses)
  totalCost: number;
  // Distance
  totalDistance: number;
  // Revenues
  revenuesCount: number;
  revenuesAmount: number;
  // Cost per distance (null if no distance recorded)
  fuelCostPerDistance: number | null;
  runningCostPerDistance: number | null;
  // Unit labels (from API, pre-converted to user preferences)
  distanceUnit: string;
  volumeUnit: string;
}

export interface CarStatsContextValue {
  // Total summaries (all-time stats per car)
  totalSummaries: CarTotalSummary[];
  totalSummariesLoading: boolean;
  totalSummariesError: string | null;
  getTotalSummaryByCarId: (
    carId: string | undefined
  ) => CarTotalSummary | CarTotalSummary[] | undefined;
  refreshTotalSummaries: () => Promise<void>;

  // Monthly summaries (selected month stats per car)
  monthlySummaries: CarMonthlySummary[];
  monthlySummariesLoading: boolean;
  monthlySummariesError: string | null;
  getMonthlySummaryByCarId: (
    carId: string | undefined
  ) => CarMonthlySummary | CarMonthlySummary[] | undefined;
  refreshMonthlySummaries: () => Promise<void>;

  // Month navigation
  selectedMonth: MonthYear;
  isCurrentMonth: boolean;
  canGoToPreviousMonth: boolean;
  canGoToNextMonth: boolean;
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;
  goToCurrentMonth: () => void;
  setSelectedMonth: (month: MonthYear) => void;

  // Combined loading state
  loading: boolean;
  isLoaded: boolean;

  // Aggregates across all cars (for summary bar)
  monthlyAggregates: MonthlyAggregates;

  // Legacy aggregates (for backwards compatibility)
  totalExpensesThisMonth: number;
  totalRefuelsThisMonth: number;
  totalRefuelsCountThisMonth: number;
  totalMileageThisMonth: number;

  // Refresh all stats
  refresh: () => Promise<void>;
  reset: () => void;
}

export interface CarStatsProviderProps {
  children: React.ReactNode;
  /** Options for total summaries hook (autoLoad, initialFilters, etc.) */
  totalSummariesOptions?: UseCarTotalSummariesOptions;
  /** Options for monthly summaries hook (autoLoad, initialFilters, etc.) */
  monthlySummariesOptions?: UseCarMonthlySummariesOptions;
}

// ============================================
// Context
// ============================================

const CarStatsContext = createContext<CarStatsContextValue | undefined>(
  undefined
);

// ============================================
// Provider
// ============================================

export const CarStatsProvider: React.FC<CarStatsProviderProps> = ({
  children,
  totalSummariesOptions = {},
  monthlySummariesOptions = {},
}) => {
  // Get user preferences directly from UserProfileContext
  const {
    currency: homeCurrency,
    distanceUnit,
    volumeUnit,
    isLoading: profileLoading,
    isLoaded: profileIsLoaded,
  } = useUserProfileContext();

  // Get active cars from CarsContext — used as the source of truth for
  // generating empty placeholders when server returns no data
  const { cars, loading: carsLoading } = useCarsContext();

  // Get current year and month
  const currentYear = dayjs().year();
  const currentMonth = dayjs().month() + 1; // dayjs months are 0-indexed

  // ============================================
  // Month Navigation State
  // ============================================

  const [selectedMonth, setSelectedMonthState] = useState<MonthYear>({
    year: currentYear,
    month: currentMonth,
  });

  // Track the earliest month with data
  const [earliestMonthWithData, setEarliestMonthWithData] =
    useState<MonthYear | null>(null);

  // Track if we've confirmed no data for a month (to stop navigation)
  const noDataMonthRef = useRef<MonthYear | null>(null);

  // Track if initial load has been done
  const initialLoadDoneRef = useRef(false);

  // ============================================
  // Total Summaries Hook
  // ============================================

  // Don't autoLoad until profile is loaded (we need homeCurrency for proper filtering)
  const mergedTotalOptions: UseCarTotalSummariesOptions = {
    autoLoad: false, // We'll trigger load manually after profile is ready
    ...totalSummariesOptions,
  };

  const totalSummariesHook = useCarTotalSummaries(mergedTotalOptions);

  const {
    carTotalSummaries: totalSummaries,
    loading: totalSummariesLoading,
    lastError: totalSummariesError,
    //refresh: refreshTotalSummaries,
    reset: resetTotalSummaries,
    getCarTotalSummaryByCarId,
    list: listTotalSummaries,
  } = totalSummariesHook;

  // ============================================
  // Monthly Summaries Hook
  // ============================================

  // Don't autoLoad until profile is loaded
  const mergedMonthlySummariesOptions: UseCarMonthlySummariesOptions = {
    autoLoad: false, // We'll trigger load manually after profile is ready
    ...monthlySummariesOptions,
  };

  const monthlySummariesHook = useCarMonthlySummaries(mergedMonthlySummariesOptions);

  const {
    carMonthlySummaries: monthlySummaries,
    loading: monthlySummariesLoading,
    lastError: monthlySummariesError,
    //refresh: refreshMonthlySummaries,
    reset: resetMonthlySummaries,
    list: listMonthlySummaries,
  } = monthlySummariesHook;

  // ============================================
  // Load data when profile is ready
  // ============================================

  // Use refs to store latest function references to avoid stale closures
  const listMonthlySummariesRef = useRef(listMonthlySummaries);
  const listTotalSummariesRef = useRef(listTotalSummaries);

  // Keep refs updated
  useEffect(() => {
    listMonthlySummariesRef.current = listMonthlySummaries;
  }, [listMonthlySummaries]);

  useEffect(() => {
    listTotalSummariesRef.current = listTotalSummaries;
  }, [listTotalSummaries]);

  const loadMonthlyData = useCallback(
    async (year: number, month: number) => {
      await listMonthlySummariesRef.current(
        { year: [year], month: [month] },
        { page: 1, pageSize: 100 },
        [{ name: "carId", order: "ASC" }],
        true
      );
    },
    [] // No dependencies - uses ref
  );

  const loadTotalSummaries = useCallback(async () => {
    await listTotalSummariesRef.current(
      {},
      { page: 1, pageSize: 100 },
      [{ name: "carId", order: "ASC" }],
      true
    );
  }, []); // No dependencies - uses ref

  // Initial load when profile becomes ready
  useEffect(() => {
    if (profileLoading || !profileIsLoaded) {
      return;
    }
    if (initialLoadDoneRef.current) {
      return;
    }

    // Profile is ready, trigger initial load
    initialLoadDoneRef.current = true;
    loadTotalSummaries();
    loadMonthlyData(selectedMonth.year, selectedMonth.month);
  }, [profileLoading, profileIsLoaded, loadTotalSummaries, loadMonthlyData, selectedMonth.year, selectedMonth.month]);

  // Load monthly data when selected month changes (after initial load)
  useEffect(() => {
    if (!initialLoadDoneRef.current) return;
    if (profileLoading || !profileIsLoaded) return;

    loadMonthlyData(selectedMonth.year, selectedMonth.month);
  }, [selectedMonth.year, selectedMonth.month, loadMonthlyData, profileLoading, profileIsLoaded]);

  // ============================================
  // Filtered monthly summaries by home currency
  // ============================================

  const filteredMonthlySummaries = useMemo(() => {
    if (!homeCurrency) return monthlySummaries;
    return monthlySummaries.filter((s) => s.homeCurrency === homeCurrency);
  }, [monthlySummaries, homeCurrency]);

  // ============================================
  // Effective monthly summaries (with empty placeholders)
  // When the server returns no data for some or all cars for the
  // selected month, generate empty CarMonthlySummary records for
  // those missing cars so the UI displays zeros instead of nothing.
  // Uses cars from CarsContext as the source of truth for
  // which cars should have summaries.
  // ============================================

  const effectiveMonthlySummaries = useMemo((): CarMonthlySummary[] => {
    // Wait until both cars and monthly data are done loading
    if (monthlySummariesLoading || carsLoading) {
      return filteredMonthlySummaries;
    }

    // No active cars — nothing to fill in
    if (cars.length === 0) {
      return filteredMonthlySummaries;
    }

    // Find which active cars are missing from the server response
    const existingCarIds = new Set(
      filteredMonthlySummaries.map((s) => s.carId).filter(Boolean)
    );

    const missingCars = cars.filter(
      (car) => car.id && !existingCarIds.has(car.id)
    );

    // If all cars already have data, no placeholders needed
    if (missingCars.length === 0) {
      return filteredMonthlySummaries;
    }

    // Generate empty summaries for missing cars
    const emptySummaries: CarMonthlySummary[] = missingCars.map(
      (car): CarMonthlySummary => ({
        id: undefined,
        carId: car.id,
        homeCurrency: homeCurrency || undefined,
        year: selectedMonth.year,
        month: selectedMonth.month,
        startMileage: 0,
        endMileage: 0,
        refuelsCount: 0,
        expensesCount: 0,
        refuelsTaxes: 0,
        refuelsCost: 0,
        expensesFees: 0,
        expensesTaxes: 0,
        expensesCost: 0,
        refuelsVolume: 0,
        revenuesCount: 0,
        revenuesAmount: 0,
        maintenanceCount: 0,
        maintenanceCost: 0,
        consumptionVolume: 0,
        isFirstRefuelMonth: false,
        checkpointsCount: 0,
        travelsCount: 0,
        travelsDistance: 0,
        firstRecordAt: undefined,
        lastRecordAt: undefined,
        updatedAt: undefined,
        car: undefined,
        inCarUnits: {
          distanceUnit: distanceUnit,
          volumeUnit: volumeUnit,
          startMileage: 0,
          endMileage: 0,
          travelsDistance: 0,
          refuelsVolume: 0,
          consumptionVolume: 0,
          consumptionUnit: undefined,
          consumption: undefined,
        },
        inUserUnits: {
          distanceUnit: distanceUnit,
          volumeUnit: volumeUnit,
          startMileage: 0,
          endMileage: 0,
          travelsDistance: 0,
          refuelsVolume: 0,
          consumptionVolume: 0,
          consumptionUnit: undefined,
          consumption: undefined,
        },
      })
    );

    return [...filteredMonthlySummaries, ...emptySummaries];
  }, [
    filteredMonthlySummaries,
    monthlySummariesLoading,
    carsLoading,
    cars,
    homeCurrency,
    selectedMonth.year,
    selectedMonth.month,
    distanceUnit,
    volumeUnit,
  ]);

  // Track earliest month with data (using filtered summaries)
  useEffect(() => {
    if (filteredMonthlySummaries.length > 0 && !monthlySummariesLoading) {
      // We have data for this month
      if (
        !earliestMonthWithData ||
        selectedMonth.year < earliestMonthWithData.year ||
        (selectedMonth.year === earliestMonthWithData.year &&
          selectedMonth.month < earliestMonthWithData.month)
      ) {
        setEarliestMonthWithData(selectedMonth);
      }
      // Clear no-data marker since we found data
      noDataMonthRef.current = null;
    } else if (filteredMonthlySummaries.length === 0 && !monthlySummariesLoading) {
      // No data for this month - mark it
      noDataMonthRef.current = selectedMonth;
    }
  }, [
    filteredMonthlySummaries,
    monthlySummariesLoading,
    selectedMonth,
    earliestMonthWithData,
  ]);

  // ============================================
  // Combined loading state
  // ============================================

  const loading = useMemo(
    () => profileLoading || carsLoading || totalSummariesLoading || monthlySummariesLoading,
    [profileLoading, carsLoading, totalSummariesLoading, monthlySummariesLoading]
  );

  const isLoaded = useMemo(
    () =>
      profileIsLoaded && !loading && (totalSummaries.length > 0 || effectiveMonthlySummaries.length > 0),
    [profileIsLoaded, loading, totalSummaries.length, effectiveMonthlySummaries.length]
  );

  // ============================================
  // Month Navigation
  // ============================================

  const isCurrentMonth = useMemo(
    () =>
      selectedMonth.year === currentYear &&
      selectedMonth.month === currentMonth,
    [selectedMonth, currentYear, currentMonth]
  );

  const canGoToNextMonth = useMemo(() => {
    // Can go forward if not at current month
    return !isCurrentMonth;
  }, [isCurrentMonth]);

  const canGoToPreviousMonth = useMemo(() => {
    // Can go back if we haven't hit a month with no data
    // or if we're not at the no-data month
    if (noDataMonthRef.current) {
      const noData = noDataMonthRef.current;
      // If current selection is the no-data month, can't go further back
      if (
        selectedMonth.year === noData.year &&
        selectedMonth.month === noData.month
      ) {
        return false;
      }
    }
    return true;
  }, [selectedMonth]);

  const goToPreviousMonth = useCallback(() => {
    if (!canGoToPreviousMonth) return;

    setSelectedMonthState((prev) => {
      const prevDate = dayjs()
        .year(prev.year)
        .month(prev.month - 1)
        .subtract(1, "month");
      return {
        year: prevDate.year(),
        month: prevDate.month() + 1,
      };
    });
  }, [canGoToPreviousMonth]);

  const goToNextMonth = useCallback(() => {
    if (!canGoToNextMonth) return;

    setSelectedMonthState((prev) => {
      const nextDate = dayjs()
        .year(prev.year)
        .month(prev.month - 1)
        .add(1, "month");

      // Don't go past current month
      if (
        nextDate.year() > currentYear ||
        (nextDate.year() === currentYear &&
          nextDate.month() + 1 > currentMonth)
      ) {
        return { year: currentYear, month: currentMonth };
      }

      return {
        year: nextDate.year(),
        month: nextDate.month() + 1,
      };
    });
  }, [canGoToNextMonth, currentYear, currentMonth]);

  const goToCurrentMonth = useCallback(() => {
    setSelectedMonthState({ year: currentYear, month: currentMonth });
  }, [currentYear, currentMonth]);

  const setSelectedMonth = useCallback((month: MonthYear) => {
    setSelectedMonthState(month);
  }, []);

  // ============================================
  // Lookup functions
  // ============================================

  const getTotalSummaryByCarId = useCallback(
    (
      carId: string | undefined
    ): CarTotalSummary | CarTotalSummary[] | undefined => {
      return getCarTotalSummaryByCarId(carId);
    },
    [getCarTotalSummaryByCarId]
  );

  const getMonthlySummaryByCarId = useCallback(
    (
      carId: string | undefined
    ): CarMonthlySummary | CarMonthlySummary[] | undefined => {
      if (!carId) return undefined;

      // Use effectiveMonthlySummaries (includes empty placeholders) so that
      // components always get a summary object even for months with no data.
      // Return ALL summaries for this car (across all currencies).
      // VehicleCardStats will aggregate physical measurements and handle currency switching.
      const summaries = effectiveMonthlySummaries.filter(
        (summary) => summary.carId === carId
      );

      return summaries;
    },
    [effectiveMonthlySummaries]
  );

  // ============================================
  // Monthly Aggregates (using pre-converted values from API)
  // ============================================

  const monthlyAggregates = useMemo((): MonthlyAggregates => {
    const aggregates: MonthlyAggregates = {
      refuelsCount: 0,
      refuelsCost: 0,
      refuelsVolume: 0,
      maintenanceCount: 0,
      maintenanceCost: 0,
      otherExpensesCount: 0,
      otherExpensesCost: 0,
      totalCost: 0,
      totalDistance: 0,
      revenuesCount: 0,
      revenuesAmount: 0,
      fuelCostPerDistance: null,
      runningCostPerDistance: null,
      // Default unit labels from user profile (will be overwritten if API provides them)
      distanceUnit: distanceUnit,
      volumeUnit: volumeUnit,
    };

    // Use effective summaries (includes empty placeholders when no server data)
    effectiveMonthlySummaries.forEach((summary, index) => {
      // Extract unit labels from the first summary's inUserUnits
      // (all summaries should have the same user units)
      if (index === 0 && summary.inUserUnits) {
        aggregates.distanceUnit =
          summary.inUserUnits.distanceUnit || distanceUnit;
        aggregates.volumeUnit =
          summary.inUserUnits.volumeUnit || volumeUnit;
      }

      // Refuels count (always from base data)
      aggregates.refuelsCount += summary.refuelsCount || 0;

      // Refuels cost (currency-based, no conversion needed)
      aggregates.refuelsCost +=
        (summary.refuelsCost || 0) + (summary.refuelsTaxes || 0);

      // Refuels volume - use pre-converted value from inUserUnits
      const refuelsVolume = summary.inUserUnits?.refuelsVolume ?? summary.refuelsVolume ?? 0;
      aggregates.refuelsVolume += refuelsVolume;

      // Maintenance (counts and costs)
      aggregates.maintenanceCount += summary.maintenanceCount || 0;
      aggregates.maintenanceCost += summary.maintenanceCost || 0;

      // Total expenses cost (includes fees and taxes)
      const totalExpensesCost =
        (summary.expensesCost || 0) +
        (summary.expensesFees || 0) +
        (summary.expensesTaxes || 0);

      // Other expenses = total expenses - maintenance
      const otherExpensesCount =
        (summary.expensesCount || 0) - (summary.maintenanceCount || 0);
      const otherExpensesCost = totalExpensesCost - (summary.maintenanceCost || 0);

      aggregates.otherExpensesCount += Math.max(0, otherExpensesCount);
      aggregates.otherExpensesCost += Math.max(0, otherExpensesCost);

      // Distance - use pre-converted values from inUserUnits
      const startMileage = summary.inUserUnits?.startMileage ?? summary.startMileage ?? 0;
      const endMileage = summary.inUserUnits?.endMileage ?? summary.endMileage ?? 0;
      const distance = endMileage - startMileage;
      aggregates.totalDistance += Math.max(0, distance);

      // Revenues (counts and amounts)
      aggregates.revenuesCount += summary.revenuesCount || 0;
      aggregates.revenuesAmount += summary.revenuesAmount || 0;
    });

    // Total cost = refuels + all expenses (maintenance + other)
    aggregates.totalCost =
      aggregates.refuelsCost +
      aggregates.maintenanceCost +
      aggregates.otherExpensesCost;

    // Calculate cost per distance (only if distance > 0)
    // Note: costs are in home currency, distance is in user's preferred unit
    if (aggregates.totalDistance > 0) {
      // Fuel cost per distance unit
      aggregates.fuelCostPerDistance =
        aggregates.refuelsCost / aggregates.totalDistance;

      // Running cost per distance (fuel + maintenance)
      aggregates.runningCostPerDistance =
        (aggregates.refuelsCost + aggregates.maintenanceCost) /
        aggregates.totalDistance;
    }

    return aggregates;
  }, [effectiveMonthlySummaries, distanceUnit, volumeUnit]);

  // ============================================
  // Legacy Aggregates (for backwards compatibility)
  // ============================================

  const totalExpensesThisMonth = useMemo(() => {
    return monthlyAggregates.totalCost;
  }, [monthlyAggregates]);

  const totalRefuelsThisMonth = useMemo(() => {
    return monthlyAggregates.refuelsVolume;
  }, [monthlyAggregates]);

  const totalRefuelsCountThisMonth = useMemo(() => {
    return monthlyAggregates.refuelsCount;
  }, [monthlyAggregates]);

  const totalMileageThisMonth = useMemo(() => {
    return monthlyAggregates.totalDistance;
  }, [monthlyAggregates]);

  // ============================================
  // Combined operations
  // ============================================

  const refresh = useCallback(async () => {
    // Don't refresh if profile isn't ready
    if (profileLoading || !profileIsLoaded) return;

    await Promise.all([
      loadTotalSummaries(),
      loadMonthlyData(selectedMonth.year, selectedMonth.month),
    ]);
  }, [loadTotalSummaries, loadMonthlyData, selectedMonth, profileLoading, profileIsLoaded]);

  const reset = useCallback(() => {
    resetTotalSummaries();
    resetMonthlySummaries();
    setSelectedMonthState({ year: currentYear, month: currentMonth });
    setEarliestMonthWithData(null);
    noDataMonthRef.current = null;
    initialLoadDoneRef.current = false;
  }, [resetTotalSummaries, resetMonthlySummaries, currentYear, currentMonth]);

  // ============================================
  // Context value
  // ============================================

  const contextValue: CarStatsContextValue = {
    // Total summaries
    totalSummaries,
    totalSummariesLoading,
    totalSummariesError,
    getTotalSummaryByCarId,
    refreshTotalSummaries: loadTotalSummaries,

    // Monthly summaries:
    // - monthlySummaries: effective summaries (real data filtered by home currency,
    //   or empty placeholders when no server data — for summary bar aggregates)
    // - getMonthlySummaryByCarId: returns summaries for a car from effective data
    //   (for VehicleCardStats aggregation)
    monthlySummaries: effectiveMonthlySummaries,
    monthlySummariesLoading,
    monthlySummariesError,
    getMonthlySummaryByCarId,
    refreshMonthlySummaries: () => loadMonthlyData(selectedMonth.year, selectedMonth.month),

    // Month navigation
    selectedMonth,
    isCurrentMonth,
    canGoToPreviousMonth,
    canGoToNextMonth,
    goToPreviousMonth,
    goToNextMonth,
    goToCurrentMonth,
    setSelectedMonth,

    // Combined state
    loading,
    isLoaded,

    // Aggregates
    monthlyAggregates,

    // Legacy aggregates
    totalExpensesThisMonth,
    totalRefuelsThisMonth,
    totalRefuelsCountThisMonth,
    totalMileageThisMonth,

    // Combined operations
    refresh,
    reset,
  };

  return (
    <CarStatsContext.Provider value={contextValue}>
      {children}
    </CarStatsContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

/**
 * Hook to access car statistics from context.
 * Must be used within CarStatsProvider.
 */
export const useCarStatsContext = (): CarStatsContextValue => {
  const context = useContext(CarStatsContext);

  if (context === undefined) {
    throw new Error(
      "useCarStatsContext must be used within a CarStatsProvider"
    );
  }

  return context;
};